<script setup lang="ts">
/**
 * An `<img>` that serves the right size for the slot it renders into.
 *
 * Pass the same path you would give a plain `<img src>`. If the file lives
 * under `theme/assets/`, this swaps in build-generated WebP variants and a
 * `srcset`; anything else (a remote URL, an unprocessed path) renders
 * unchanged, so it is always safe to use.
 *
 * `sizes` is required and worth getting right — it is how the browser knows
 * which variant to fetch, and a wrong value is the difference between a 10 KB
 * and a 100 KB download. Describe the slot's rendered width, e.g.
 * `"(min-width: 48rem) 560px, 100vw"`.
 *
 * Pass `width`/`height` whenever the intrinsic aspect ratio is known: they
 * reserve space before the image loads and prevent layout shift.
 */
import { computed } from "vue";
import { resolveImage } from "../assets/responsiveImages";

const props = withDefaults(
  defineProps<{
    /** Image path. Resolved against `theme/assets/` by filename. */
    src: string | undefined;
    alt: string;
    /** Rendered width of the slot, as a CSS `sizes` descriptor. */
    sizes: string;
    /** Intrinsic dimensions, used to reserve layout space. */
    width?: number | string;
    height?: number | string;
    loading?: "lazy" | "eager";
    fetchpriority?: "high" | "low" | "auto";
  }>(),
  {
    width: undefined,
    height: undefined,
    loading: "lazy",
    fetchpriority: "auto",
  },
);

const image = computed(() => resolveImage(props.src));
</script>

<template>
  <img
    :src="image.src"
    :srcset="image.srcset"
    :sizes="sizes"
    :alt="alt"
    :width="width"
    :height="height"
    :loading="loading"
    :fetchpriority="fetchpriority"
    decoding="async"
  />
</template>
